import { TRPCClientErrorLike } from "@trpc/client";
import { UseTRPCQueryResult } from "@trpc/react-query/shared";

import type {
  IAsyncWorkflowWarningPage,
  ICanonicalBootstrapAdminState,
  ICanonicalBootstrapApprovedTagPage,
  ICanonicalBootstrapApprovedTagParentOptionList,
  ICanonicalBootstrapValidationBlockerList,
  ICanonicalDuplicateFixApplyResult,
  ICanonicalDuplicateGroupList,
  ICanonicalHierarchyApprovalMutationResult,
  ICanonicalHierarchyPendingProposalPage,
  ICanonicalHierarchySynthesizedParentUpdateMutationResult,
  ICanonicalBootstrapPendingProposalPage,
  ICanonicalHierarchyRun,
  ICanonicalTagApprovalMutationResult,
  ICanonicalTagHierarchyUpdateMutationResult,
  ITagAliasBootstrapAdminState,
  ITagAliasPendingDecisionPage,
  ITagAliasSnapshot,
  ITaxonomyCleanupJob,
  ITaxonomySyncRun,
} from "@monorepo/core";
import type { AppRouter } from "@monorepo/lambda-trpc";

import { trpc } from "../utils/trpc";

const NON_PERSISTED_QUERY_META = {
  persist: false,
} as const;

const NON_BATCHED_TRPC_CONTEXT = {
  skipBatch: true,
} as const;

type CleanupCanonicalBootstrapWorkspaceMutation = ReturnType<
  typeof trpc.taxonomy.cleanupCanonicalBootstrapWorkspace.useMutation
>;
type StartCanonicalBootstrapMutation = ReturnType<
  typeof trpc.taxonomy.startCanonicalBootstrap.useMutation
>;
type StopCanonicalBootstrapMutation = ReturnType<
  typeof trpc.taxonomy.stopCanonicalBootstrapRun.useMutation
>;
type GenerateCanonicalProposalsMutation = ReturnType<
  typeof trpc.taxonomy.generateCanonicalProposals.useMutation
>;
type GenerateCanonicalHierarchyMutation = ReturnType<
  typeof trpc.taxonomy.generateCanonicalHierarchy.useMutation
>;
type ApproveCanonicalProposalMutation = ReturnType<
  typeof trpc.taxonomy.approveCanonicalProposal.useMutation
>;
type ApproveCanonicalProposalDuplicateFixMutation = ReturnType<
  typeof trpc.taxonomy.approveCanonicalProposalDuplicateFix.useMutation
>;
type ApproveAllCanonicalProposalDuplicateFixesMutation = ReturnType<
  typeof trpc.taxonomy.approveAllCanonicalProposalDuplicateFixes.useMutation
>;
type ApproveRemainingCanonicalProposalsMutation = ReturnType<
  typeof trpc.taxonomy.approveRemainingCanonicalProposals.useMutation
>;
type ApproveCanonicalHierarchyProposalMutation = ReturnType<
  typeof trpc.taxonomy.approveCanonicalHierarchyProposal.useMutation
>;
type ApproveCanonicalHierarchyDuplicateFixMutation = ReturnType<
  typeof trpc.taxonomy.approveCanonicalHierarchyDuplicateFix.useMutation
>;
type ApproveAllCanonicalHierarchyDuplicateFixesMutation = ReturnType<
  typeof trpc.taxonomy.approveAllCanonicalHierarchyDuplicateFixes.useMutation
>;
type ApproveRemainingCanonicalHierarchyProposalsMutation = ReturnType<
  typeof trpc.taxonomy.approveRemainingCanonicalHierarchyProposals.useMutation
>;
type RejectCanonicalHierarchyProposalMutation = ReturnType<
  typeof trpc.taxonomy.rejectCanonicalHierarchyProposal.useMutation
>;
type RejectCanonicalProposalMutation = ReturnType<
  typeof trpc.taxonomy.rejectCanonicalProposal.useMutation
>;
type UpdateCanonicalTagHierarchyMutation = ReturnType<
  typeof trpc.taxonomy.updateCanonicalTagHierarchy.useMutation
>;
type UpdateApprovedSynthesizedHierarchyParentMutation = ReturnType<
  typeof trpc.taxonomy.updateApprovedSynthesizedHierarchyParent.useMutation
>;
type PublishCanonicalSnapshotMutation = ReturnType<
  typeof trpc.taxonomy.publishCanonicalSnapshot.useMutation
>;
type StartAliasBootstrapMutation = ReturnType<
  typeof trpc.taxonomy.startAliasBootstrap.useMutation
>;
type ApproveTagAliasDecisionMutation = ReturnType<
  typeof trpc.taxonomy.approveTagAliasDecision.useMutation
>;
type RejectTagAliasDecisionMutation = ReturnType<
  typeof trpc.taxonomy.rejectTagAliasDecision.useMutation
>;
type PublishTagAliasSnapshotMutation = ReturnType<
  typeof trpc.taxonomy.publishTagAliasSnapshot.useMutation
>;

interface CanonicalBootstrapAdminActions {
  cleanupWorkspaceMutation: CleanupCanonicalBootstrapWorkspaceMutation;
  startBootstrapMutation: StartCanonicalBootstrapMutation;
  stopBootstrapMutation: StopCanonicalBootstrapMutation;
  generateCanonicalProposalsMutation: GenerateCanonicalProposalsMutation;
  generateCanonicalHierarchyMutation: GenerateCanonicalHierarchyMutation;
  approveCanonicalProposalMutation: ApproveCanonicalProposalMutation;
  approveCanonicalProposalDuplicateFixMutation: ApproveCanonicalProposalDuplicateFixMutation;
  approveAllCanonicalProposalDuplicateFixesMutation: ApproveAllCanonicalProposalDuplicateFixesMutation;
  approveRemainingCanonicalProposalsMutation: ApproveRemainingCanonicalProposalsMutation;
  approveCanonicalHierarchyProposalMutation: ApproveCanonicalHierarchyProposalMutation;
  approveCanonicalHierarchyDuplicateFixMutation: ApproveCanonicalHierarchyDuplicateFixMutation;
  approveAllCanonicalHierarchyDuplicateFixesMutation: ApproveAllCanonicalHierarchyDuplicateFixesMutation;
  approveRemainingCanonicalHierarchyProposalsMutation: ApproveRemainingCanonicalHierarchyProposalsMutation;
  rejectCanonicalHierarchyProposalMutation: RejectCanonicalHierarchyProposalMutation;
  rejectCanonicalProposalMutation: RejectCanonicalProposalMutation;
  updateCanonicalTagHierarchyMutation: UpdateCanonicalTagHierarchyMutation;
  updateApprovedSynthesizedHierarchyParentMutation: UpdateApprovedSynthesizedHierarchyParentMutation;
  publishCanonicalSnapshotMutation: PublishCanonicalSnapshotMutation;
  invalidate: () => Promise<void>;
}

interface TagAliasBootstrapAdminActions {
  startBootstrapMutation: StartAliasBootstrapMutation;
  approveTagAliasDecisionMutation: ApproveTagAliasDecisionMutation;
  rejectTagAliasDecisionMutation: RejectTagAliasDecisionMutation;
  publishTagAliasSnapshotMutation: PublishTagAliasSnapshotMutation;
  invalidate: () => Promise<void>;
}

export function useCanonicalBootstrapAdminState(): UseTRPCQueryResult<
  ICanonicalBootstrapAdminState,
  TRPCClientErrorLike<AppRouter>
> {
  return trpc.taxonomy.getCanonicalBootstrapState.useQuery(undefined, {
    refetchOnWindowFocus: false,
    meta: NON_PERSISTED_QUERY_META,
    trpc: {
      context: NON_BATCHED_TRPC_CONTEXT,
    },
  });
}

export function useTagAliasBootstrapAdminState(): UseTRPCQueryResult<
  ITagAliasBootstrapAdminState,
  TRPCClientErrorLike<AppRouter>
> {
  return trpc.taxonomy.getTagAliasBootstrapState.useQuery(undefined, {
    refetchOnWindowFocus: false,
    meta: NON_PERSISTED_QUERY_META,
    trpc: {
      context: NON_BATCHED_TRPC_CONTEXT,
    },
  });
}

export function useCanonicalBootstrapValidationBlockers(input: {
  runId?: string;
}): UseTRPCQueryResult<
  ICanonicalBootstrapValidationBlockerList,
  TRPCClientErrorLike<AppRouter>
> {
  return trpc.taxonomy.listCanonicalBootstrapValidationBlockers.useQuery(
    {
      runId: input.runId ?? "",
    },
    {
      enabled: Boolean(input.runId),
      refetchOnWindowFocus: false,
      meta: NON_PERSISTED_QUERY_META,
    },
  );
}

export function useAsyncWorkflowWarnings(input: {
  workflowType: "TAXONOMY_SYNC_RUN" | "USER_IMPORT";
  workflowId: string;
  cursor?: string;
  pageSize?: number;
  enabled?: boolean;
}): UseTRPCQueryResult<
  IAsyncWorkflowWarningPage,
  TRPCClientErrorLike<AppRouter>
> {
  return trpc.taxonomy.listAsyncWorkflowWarnings.useQuery(
    {
      workflowType: input.workflowType,
      workflowId: input.workflowId,
      cursor: input.cursor,
      pageSize: input.pageSize,
    },
    {
      enabled: input.enabled ?? true,
      refetchOnWindowFocus: false,
      meta: NON_PERSISTED_QUERY_META,
      trpc: {
        context: NON_BATCHED_TRPC_CONTEXT,
      },
    },
  );
}

export function usePendingCanonicalBootstrapProposals(input?: {
  cursor?: string;
  pageSize?: number;
  search?: string;
}): UseTRPCQueryResult<
  ICanonicalBootstrapPendingProposalPage,
  TRPCClientErrorLike<AppRouter>
> {
  return trpc.taxonomy.listPendingCanonicalBootstrapProposals.useQuery(input, {
    refetchOnWindowFocus: false,
    meta: NON_PERSISTED_QUERY_META,
    trpc: {
      context: NON_BATCHED_TRPC_CONTEXT,
    },
  });
}

export function useCanonicalProposalDuplicateGroups(input: {
  runId?: string;
}): UseTRPCQueryResult<
  ICanonicalDuplicateGroupList,
  TRPCClientErrorLike<AppRouter>
> {
  return trpc.taxonomy.listCanonicalProposalDuplicateGroups.useQuery(
    {
      runId: input.runId ?? "",
    },
    {
      enabled: Boolean(input.runId),
      refetchOnWindowFocus: false,
      meta: NON_PERSISTED_QUERY_META,
      trpc: {
        context: NON_BATCHED_TRPC_CONTEXT,
      },
    },
  );
}

export function useApprovedCanonicalBootstrapTags(input?: {
  cursor?: string;
  pageSize?: number;
  search?: string;
  layer?: "FUNCTIONAL" | "TRIBAL" | "THEME";
  parentSlug?: string;
}): UseTRPCQueryResult<
  ICanonicalBootstrapApprovedTagPage,
  TRPCClientErrorLike<AppRouter>
> {
  return trpc.taxonomy.listApprovedCanonicalBootstrapTags.useQuery(input, {
    refetchOnWindowFocus: false,
    meta: NON_PERSISTED_QUERY_META,
    trpc: {
      context: NON_BATCHED_TRPC_CONTEXT,
    },
  });
}

export function useApprovedCanonicalBootstrapTagParentOptions(input?: {
  layer?: "FUNCTIONAL" | "TRIBAL" | "THEME";
}): UseTRPCQueryResult<
  ICanonicalBootstrapApprovedTagParentOptionList,
  TRPCClientErrorLike<AppRouter>
> {
  return trpc.taxonomy.listApprovedCanonicalBootstrapTagParentOptions.useQuery(
    input,
    {
      refetchOnWindowFocus: false,
      meta: NON_PERSISTED_QUERY_META,
      trpc: {
        context: NON_BATCHED_TRPC_CONTEXT,
      },
    },
  );
}

export function usePendingCanonicalHierarchyProposals(input?: {
  cursor?: string;
  pageSize?: number;
  search?: string;
}): UseTRPCQueryResult<
  ICanonicalHierarchyPendingProposalPage,
  TRPCClientErrorLike<AppRouter>
> {
  return trpc.taxonomy.listPendingCanonicalHierarchyProposals.useQuery(input, {
    refetchOnWindowFocus: false,
    meta: NON_PERSISTED_QUERY_META,
    trpc: {
      context: NON_BATCHED_TRPC_CONTEXT,
    },
  });
}

export function useCanonicalHierarchyDuplicateGroups(input: {
  runId?: string;
}): UseTRPCQueryResult<
  ICanonicalDuplicateGroupList,
  TRPCClientErrorLike<AppRouter>
> {
  return trpc.taxonomy.listCanonicalHierarchyDuplicateGroups.useQuery(
    {
      runId: input.runId ?? "",
    },
    {
      enabled: Boolean(input.runId),
      refetchOnWindowFocus: false,
      meta: NON_PERSISTED_QUERY_META,
      trpc: {
        context: NON_BATCHED_TRPC_CONTEXT,
      },
    },
  );
}

export function usePendingTagAliasDecisions(input?: {
  cursor?: string;
  pageSize?: number;
  search?: string;
}): UseTRPCQueryResult<
  ITagAliasPendingDecisionPage,
  TRPCClientErrorLike<AppRouter>
> {
  return trpc.taxonomy.listPendingTagAliasDecisions.useQuery(input, {
    refetchOnWindowFocus: false,
    meta: NON_PERSISTED_QUERY_META,
  });
}

export function useCanonicalBootstrapAdminActions(): CanonicalBootstrapAdminActions {
  const utils = trpc.useUtils();

  const invalidate = async () => {
    await Promise.all([
      utils.taxonomy.getCanonicalBootstrapState.invalidate(),
      utils.taxonomy.listAsyncWorkflowWarnings.invalidate(),
      utils.taxonomy.listPendingCanonicalBootstrapProposals.invalidate(),
      utils.taxonomy.listCanonicalProposalDuplicateGroups.invalidate(),
      utils.taxonomy.listPendingCanonicalHierarchyProposals.invalidate(),
      utils.taxonomy.listCanonicalHierarchyDuplicateGroups.invalidate(),
      utils.taxonomy.listApprovedCanonicalBootstrapTags.invalidate(),
      utils.taxonomy.listApprovedCanonicalBootstrapTagParentOptions.invalidate(),
      utils.taxonomy.listCanonicalBootstrapValidationBlockers.invalidate(),
      utils.taxonomy.listPendingReviewQueue.invalidate(),
    ]);
  };

  const cleanupWorkspaceMutation =
    trpc.taxonomy.cleanupCanonicalBootstrapWorkspace.useMutation({
      onSuccess: async (_result: ITaxonomyCleanupJob) => {
        await invalidate();
      },
    });

  const startBootstrapMutation =
    trpc.taxonomy.startCanonicalBootstrap.useMutation({
      onSuccess: async () => {
        await invalidate();
      },
    });

  const stopBootstrapMutation =
    trpc.taxonomy.stopCanonicalBootstrapRun.useMutation({
      onSuccess: async () => {
        await invalidate();
      },
    });

  const generateCanonicalProposalsMutation =
    trpc.taxonomy.generateCanonicalProposals.useMutation({
      onSuccess: async () => {
        await invalidate();
      },
    });

  const generateCanonicalHierarchyMutation =
    trpc.taxonomy.generateCanonicalHierarchy.useMutation({
      onSuccess: async (_run: ICanonicalHierarchyRun) => {
        await invalidate();
      },
    });

  const approveCanonicalProposalMutation =
    trpc.taxonomy.approveCanonicalProposal.useMutation({
      onSuccess: async (result: ICanonicalTagApprovalMutationResult) => {
        if (result.outcome !== "APPROVED") {
          return;
        }
        await invalidate();
      },
    });

  const approveCanonicalProposalDuplicateFixMutation =
    trpc.taxonomy.approveCanonicalProposalDuplicateFix.useMutation({
      onSuccess: async (_result: ICanonicalDuplicateFixApplyResult) => {
        await invalidate();
      },
    });

  const approveAllCanonicalProposalDuplicateFixesMutation =
    trpc.taxonomy.approveAllCanonicalProposalDuplicateFixes.useMutation({
      onSuccess: async (_result: ICanonicalDuplicateFixApplyResult) => {
        await invalidate();
      },
    });

  const approveRemainingCanonicalProposalsMutation =
    trpc.taxonomy.approveRemainingCanonicalProposals.useMutation({
      onSuccess: async () => {
        await invalidate();
      },
    });

  const approveCanonicalHierarchyProposalMutation =
    trpc.taxonomy.approveCanonicalHierarchyProposal.useMutation({
      onSuccess: async (result: ICanonicalHierarchyApprovalMutationResult) => {
        if (result.outcome !== "APPROVED") {
          return;
        }
        await invalidate();
      },
    });

  const approveCanonicalHierarchyDuplicateFixMutation =
    trpc.taxonomy.approveCanonicalHierarchyDuplicateFix.useMutation({
      onSuccess: async (_result: ICanonicalDuplicateFixApplyResult) => {
        await invalidate();
      },
    });

  const approveAllCanonicalHierarchyDuplicateFixesMutation =
    trpc.taxonomy.approveAllCanonicalHierarchyDuplicateFixes.useMutation({
      onSuccess: async (_result: ICanonicalDuplicateFixApplyResult) => {
        await invalidate();
      },
    });

  const approveRemainingCanonicalHierarchyProposalsMutation =
    trpc.taxonomy.approveRemainingCanonicalHierarchyProposals.useMutation({
      onSuccess: async () => {
        await invalidate();
      },
    });

  const rejectCanonicalHierarchyProposalMutation =
    trpc.taxonomy.rejectCanonicalHierarchyProposal.useMutation({
      onSuccess: async () => {
        await invalidate();
      },
    });

  const rejectCanonicalProposalMutation =
    trpc.taxonomy.rejectCanonicalProposal.useMutation({
      onSuccess: async () => {
        await invalidate();
      },
    });

  const updateCanonicalTagHierarchyMutation =
    trpc.taxonomy.updateCanonicalTagHierarchy.useMutation({
      onSuccess: async (result: ICanonicalTagHierarchyUpdateMutationResult) => {
        if (result.outcome !== "UPDATED") {
          return;
        }
        await invalidate();
      },
    });

  const updateApprovedSynthesizedHierarchyParentMutation =
    trpc.taxonomy.updateApprovedSynthesizedHierarchyParent.useMutation({
      onSuccess: async (
        result: ICanonicalHierarchySynthesizedParentUpdateMutationResult,
      ) => {
        if (result.outcome !== "UPDATED") {
          return;
        }
        await invalidate();
      },
    });

  const publishCanonicalSnapshotMutation =
    trpc.taxonomy.publishCanonicalSnapshot.useMutation({
      onSuccess: async (_run: ITaxonomySyncRun) => {
        await invalidate();
      },
    });

  return {
    cleanupWorkspaceMutation,
    startBootstrapMutation,
    stopBootstrapMutation,
    generateCanonicalProposalsMutation,
    generateCanonicalHierarchyMutation,
    approveCanonicalProposalMutation,
    approveCanonicalProposalDuplicateFixMutation,
    approveAllCanonicalProposalDuplicateFixesMutation,
    approveRemainingCanonicalProposalsMutation,
    approveCanonicalHierarchyProposalMutation,
    approveCanonicalHierarchyDuplicateFixMutation,
    approveAllCanonicalHierarchyDuplicateFixesMutation,
    approveRemainingCanonicalHierarchyProposalsMutation,
    rejectCanonicalHierarchyProposalMutation,
    rejectCanonicalProposalMutation,
    updateCanonicalTagHierarchyMutation,
    updateApprovedSynthesizedHierarchyParentMutation,
    publishCanonicalSnapshotMutation,
    invalidate,
  };
}

export function useTagAliasBootstrapAdminActions(): TagAliasBootstrapAdminActions {
  const utils = trpc.useUtils();

  const invalidate = async () => {
    await Promise.all([
      utils.taxonomy.getTagAliasBootstrapState.invalidate(),
      utils.taxonomy.listPendingTagAliasDecisions.invalidate(),
      utils.taxonomy.listPendingReviewQueue.invalidate(),
    ]);
  };

  const startBootstrapMutation = trpc.taxonomy.startAliasBootstrap.useMutation({
    onSuccess: async () => {
      await invalidate();
    },
  });

  const approveTagAliasDecisionMutation =
    trpc.taxonomy.approveTagAliasDecision.useMutation({
      onSuccess: async () => {
        await invalidate();
      },
    });

  const rejectTagAliasDecisionMutation =
    trpc.taxonomy.rejectTagAliasDecision.useMutation({
      onSuccess: async () => {
        await invalidate();
      },
    });

  const publishTagAliasSnapshotMutation =
    trpc.taxonomy.publishTagAliasSnapshot.useMutation({
      onSuccess: async (_snapshot: ITagAliasSnapshot) => {
        await invalidate();
      },
    });

  return {
    startBootstrapMutation,
    approveTagAliasDecisionMutation,
    rejectTagAliasDecisionMutation,
    publishTagAliasSnapshotMutation,
    invalidate,
  };
}
